import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { climbScansService } from '../services/climb-scans';
import { useAuth } from '../hooks/useAuth';
import { gymsService } from '../services/gyms';
import type { ClimbScan } from '../types/climb-scan';

function StatusBadge({ status }: { status: ClimbScan['status'] }) {
  const { t } = useTranslation();
  const colors: Record<ClimbScan['status'], string> = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    ready: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status]}`}>
      {t(`climbScan.status.${status}`)}
    </span>
  );
}

function hasUnprocessedColors(scan: ClimbScan): boolean {
  if (scan.status !== 'ready') return true;
  return scan.colors.some(c => c.status !== 'completed');
}

/**
 * Whether there is anything behind the row to go to.
 *
 * A photo that is waiting to be looked at, or being looked at, has no colours
 * and so no climbs to work on — opening it is a page that says "wait". It stays
 * on the list with its badge, because the office wants to see that the photo
 * arrived, and becomes a door once the analysis lands. A failed one is worth
 * opening: it has the error on it.
 */
function isOpenable(scan: ClimbScan): boolean {
  return scan.status === 'ready' || scan.status === 'failed';
}

export default function GymScansListPage() {
  const { gymSlug } = useParams<{ gymSlug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [scans, setScans] = useState<ClimbScan[]>([]);
  const [gymName, setGymName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const currentUserStaff = user?.staff?.find(s => s.gym_slug === gymSlug) ?? null;
  const isStaffMember = currentUserStaff?.status === 'confirmed' &&
    (currentUserStaff?.staff_roles.includes('admin') || currentUserStaff?.staff_roles.includes('editor'));

  useEffect(() => {
    if (!gymSlug) return;
    Promise.all([
      climbScansService.getScans(gymSlug),
      gymsService.getGymBySlug(gymSlug).catch(() => null),
    ]).then(([scanData, gym]) => {
      setScans(scanData.filter(hasUnprocessedColors));
      if (gym) setGymName(gym.name);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [gymSlug]);

  useBreadcrumb(
    () => ({
      onBack: () => navigate(`/gyms/${gymSlug}`),
      segments: gymSlug
        ? [
            { label: gymName ?? gymSlug, onClick: () => navigate(`/gyms/${gymSlug}`) },
            { label: t('climbScan.scans') },
          ]
        : [],
      loading,
    }),
    [gymSlug, gymName, loading, t]
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('climbScan.scans')}</h1>
        {isStaffMember && (
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={() => navigate(`/gyms/${gymSlug}`)}
          >
            <Plus className="w-4 h-4" />
            {t('climbScan.uploadButton')}
          </Button>
        )}
      </div>

      {scans.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            {t('climbScan.noActiveScans')}
          </CardContent>
        </Card>
      ) : (
        scans.map(scan => {
          const pendingCount = scan.colors.filter(c => c.status !== 'completed').length;
          const openable = isOpenable(scan);
          return (
            <Card
              key={scan.id}
              className={
                openable
                  ? 'cursor-pointer hover:bg-muted/30 transition-colors'
                  : // Not a door and not pretending to be one: no pointer, no
                    // hover, and the photo set back so the row reads as "not
                    // yet" rather than as a link that does nothing.
                    'opacity-60'
              }
              onClick={openable ? () => navigate(`/gyms/${gymSlug}/scans/${scan.id}`) : undefined}
            >
              <CardContent className="p-4 flex items-center gap-4">
                {scan.image_url && (
                  <img
                    src={scan.image_url}
                    alt={t('climbScan.scanImage')}
                    className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium capitalize">{scan.climb_type}</span>
                    <StatusBadge status={scan.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(scan.created_at).toLocaleDateString()}
                  </p>
                  {scan.status === 'ready' && pendingCount > 0 && (
                    <p className="text-xs text-primary mt-1">
                      {t('climbScan.pendingColors', { count: pendingCount })}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
